import Phaser from 'phaser';
import { DinoId, DINOS } from '../data/dinos';
import { Sfx } from './Sfx';
import { PowerUpKind, POWERUP_DURATION_MS } from './PowerUp';

const HURT_DURATION = 280;
const INVINCIBLE_AFTER_HURT = 850;
const SHOOT_COOLDOWN = 200;
const EMPTY_COOLDOWN = 350; // throttle the empty-click sound when out of ammo
const SHOOT_RECOIL_FRAMES = 90; // ms — brief animation lockout when shooting
const MAX_AMMO = 99;

/** Player intent surface — produced by GameScene from kbd + touch input. */
export interface DinoInput {
  moveX: number;
  jumpPressed: boolean;
  jumpHeld: boolean;
  shootPressed: boolean;
}

export type DinoState = 'idle' | 'run' | 'jump' | 'shoot' | 'hurt' | 'dead';

/**
 * Event fired when the dino actually shoots. GameScene listens for this and
 * spawns/launches a bullet from the indicated muzzle position.
 */
export interface ShootEvent {
  x: number;
  y: number;
  dir: -1 | 1;
}

export class Dino extends Phaser.Physics.Arcade.Sprite {
  readonly dinoId: DinoId;
  readonly maxHp: number;
  hp: number;
  state: DinoState = 'idle';
  private invincibleUntil = 0;
  private shootRecoilUntil = 0;
  private nextShootAt = 0;
  private nextEmptyClickAt = 0;
  private hurtUntil = 0;
  private jumping = false;
  /** -1 left, 1 right — facing direction for muzzle aim */
  private facingX: -1 | 1 = 1;

  /** Number of bullets currently in the clip. Each star pickup adds one.
   *  Starts at 1 so every level (including each gauntlet leg) opens with
   *  a single shot in reserve — without it, players who immediately
   *  encounter an enemy before reaching a star are forced to stomp or
   *  flee. One starter bullet is enough to handle that first emergency. */
  ammo = 1;

  // Power-up state.
  private rapidUntil = 0;
  private tripleUntil = 0;
  private magnetUntil = 0;
  shieldOn = false;
  private shieldRing: Phaser.GameObjects.Image | null = null;

  /** UI listener — fires when a power-up is acquired or expires. */
  onPowerUpChange?: (kind: PowerUpKind, on: boolean, untilMs: number) => void;

  /** Listeners for shoot events (GameScene subscribes once). */
  onShoot?: (e: ShootEvent) => void;
  /** Fired whenever ammo changes (for HUD). */
  onAmmoChange?: (ammo: number) => void;

  addAmmo(n: number) {
    this.ammo = Math.min(MAX_AMMO, this.ammo + n);
    this.onAmmoChange?.(this.ammo);
  }

  /** Apply a power-up. Idempotent for shield, refreshes timer for the rest. */
  applyPowerUp(kind: PowerUpKind) {
    const now = this.scene.time.now;
    const dur = POWERUP_DURATION_MS[kind];
    switch (kind) {
      case 'rapid':
        this.rapidUntil = Math.max(this.rapidUntil, now + dur);
        break;
      case 'triple':
        this.tripleUntil = Math.max(this.tripleUntil, now + dur);
        break;
      case 'magnet':
        this.magnetUntil = Math.max(this.magnetUntil, now + dur);
        break;
      case 'shield':
        this.shieldOn = true;
        if (!this.shieldRing) {
          this.shieldRing = this.scene.add
            .image(this.x, this.y - 18, 'star')
            .setBlendMode(Phaser.BlendModes.ADD)
            .setTint(0x4ac8ff)
            .setScale(5)
            .setAlpha(0.5)
            .setDepth((this.depth || 0) - 1);
          this.scene.tweens.add({
            targets: this.shieldRing,
            scale: { from: 5, to: 5.6 },
            alpha: { from: 0.6, to: 0.3 },
            duration: 600,
            yoyo: true,
            repeat: -1,
          });
        }
        break;
    }
    this.onPowerUpChange?.(kind, true, kind === 'shield' ? Infinity : now + dur);
  }

  /** True while a powerup is active — used by HUD/Status effects. */
  hasRapid() {
    return this.scene.time.now < this.rapidUntil;
  }
  hasTriple() {
    return this.scene.time.now < this.tripleUntil;
  }
  hasMagnet() {
    return this.scene.time.now < this.magnetUntil;
  }

  constructor(scene: Phaser.Scene, x: number, y: number, id: DinoId) {
    super(scene, x, y, `dino-${id}`);
    this.dinoId = id;
    const stats = DINOS[id];
    this.maxHp = stats.maxHp;
    this.hp = stats.maxHp;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setOrigin(0.5, 1);
    this.setScale(2);
    this.setCollideWorldBounds(false);

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(14, 20);
    body.setOffset(5, 4);

    this.play(`${id}-idle`);
  }

  isInvincible(): boolean {
    return this.scene.time.now < this.invincibleUntil;
  }

  isHurt(): boolean {
    return this.scene.time.now < this.hurtUntil;
  }

  isDead(): boolean {
    return this.state === 'dead';
  }

  takeDamage(amount: number, fromX: number): boolean {
    if (this.isDead() || this.isInvincible()) return false;
    // God mode for playtesting: ?god in URL or window.__god === true.
    if (
      typeof window !== 'undefined' &&
      ((window as unknown as { __god?: boolean }).__god ||
        window.location.search.includes('god'))
    ) {
      return false;
    }
    // Shield power-up: absorbs one hit and pops, no damage taken.
    if (this.shieldOn) {
      this.shieldOn = false;
      this.invincibleUntil = this.scene.time.now + INVINCIBLE_AFTER_HURT;
      if (this.shieldRing) {
        const ring = this.shieldRing;
        this.shieldRing = null;
        this.scene.tweens.add({
          targets: ring,
          scale: 8,
          alpha: 0,
          duration: 240,
          onComplete: () => ring.destroy(),
        });
      }
      this.onPowerUpChange?.('shield', false, 0);
      Sfx.hit();
      void fromX;
      void amount;
      return false;
    }
    this.hp = Math.max(0, this.hp - amount);
    this.hurtUntil = this.scene.time.now + HURT_DURATION;
    this.invincibleUntil = this.scene.time.now + INVINCIBLE_AFTER_HURT;

    const dir = this.x < fromX ? -1 : 1;
    this.setVelocity(dir * 140, -260);
    this.play(`${this.dinoId}-hurt`, true);
    Sfx.hurt();

    if (this.hp <= 0) {
      this.die();
    } else {
      this.state = 'hurt';
      this.scene.tweens.add({
        targets: this,
        alpha: { from: 0.2, to: 1 },
        duration: 90,
        repeat: Math.floor(INVINCIBLE_AFTER_HURT / 180),
        yoyo: true,
        onComplete: () => this.setAlpha(1),
      });
    }
    return true;
  }

  die() {
    // God mode: bounce, refill, ignore. Dino cannot die while __god is on.
    if (
      typeof window !== 'undefined' &&
      (window as unknown as { __god?: boolean }).__god
    ) {
      this.hp = this.maxHp;
      this.setVelocity(0, -260);
      return;
    }
    // Cleanup: power-up visuals shouldn't outlive the dino.
    if (this.shieldRing) {
      this.shieldRing.destroy();
      this.shieldRing = null;
    }
    this.state = 'dead';
    this.setVelocity(0, -340);
    this.play(`${this.dinoId}-hurt`, true);
    Sfx.death();
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.checkCollision.none = true;
    this.setTint(0x9988aa);
    this.scene.tweens.add({
      targets: this,
      angle: 540,
      duration: 900,
      ease: 'Cubic.easeIn',
    });
  }

  /**
   * Drive the dino with composed input. Returns nothing; emits onShoot when
   * a bullet should spawn.
   */
  drive(input: DinoInput) {
    if (this.isDead()) return;
    const stats = DINOS[this.dinoId];
    const body = this.body as Phaser.Physics.Arcade.Body;
    const onGround = body.blocked.down || body.touching.down;
    const now = this.scene.time.now;

    // Hurt lockout — short period where input is ignored.
    if (this.isHurt()) return;

    // Movement.
    if (Math.abs(input.moveX) > 0.15) {
      this.setVelocityX(input.moveX * stats.speed);
      this.facingX = input.moveX < 0 ? -1 : 1;
      this.setFlipX(this.facingX < 0);
    } else {
      this.setVelocityX(0);
    }

    // Jump.
    if (input.jumpPressed && onGround) {
      this.setVelocityY(stats.jumpVelocity);
      this.jumping = true;
      Sfx.jump();
    }
    // Variable jump cut: tap = short hop, hold = full leap.
    if (this.jumping && !input.jumpHeld && body.velocity.y < -120) {
      this.setVelocityY(body.velocity.y * 0.5);
      this.jumping = false;
    }
    if (onGround) this.jumping = false;

    // Shoot.
    if (input.shootPressed && now >= this.nextShootAt) {
      this.shoot(now);
    }

    // Animation choice — shoot recoil takes priority briefly so the kick anim
    // plays as a "fire" pose; otherwise pick based on physical state.
    if (now < this.shootRecoilUntil) {
      // brief shoot pose — keep current frame, no anim swap
    } else if (!onGround) {
      this.state = 'jump';
      this.play(`${this.dinoId}-jump`, true);
    } else if (Math.abs(body.velocity.x) > 5) {
      this.state = 'run';
      this.play(`${this.dinoId}-run`, true);
    } else {
      this.state = 'idle';
      this.play(`${this.dinoId}-idle`, true);
    }
  }

  private shoot(now: number) {
    if (this.ammo <= 0) {
      // Empty click, throttled so it doesn't spam while X is held.
      if (now >= this.nextEmptyClickAt) {
        this.nextEmptyClickAt = now + EMPTY_COOLDOWN;
        Sfx.empty();
      }
      // Still bump the cooldown a little so we don't recheck every frame
      this.nextShootAt = now + 60;
      return;
    }
    this.ammo -= 1;
    this.onAmmoChange?.(this.ammo);
    // Rapid-fire shrinks cooldown dramatically while active.
    const cd = this.hasRapid() ? 70 : SHOOT_COOLDOWN;
    this.nextShootAt = now + cd;
    this.shootRecoilUntil = now + SHOOT_RECOIL_FRAMES;
    this.play(`${this.dinoId}-kick`, true);
    Sfx.shoot();
    this.scene.tweens.add({
      targets: this,
      x: this.x - this.facingX * 2,
      duration: 50,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
    if (this.onShoot) {
      const muzzleX = this.x + this.facingX * 14;
      const muzzleY = this.y - 18;
      // Triple-shot fires three parallel bullets stacked vertically.
      if (this.hasTriple()) {
        this.onShoot({ x: muzzleX, y: muzzleY - 7, dir: this.facingX });
        this.onShoot({ x: muzzleX, y: muzzleY, dir: this.facingX });
        this.onShoot({ x: muzzleX, y: muzzleY + 7, dir: this.facingX });
      } else {
        this.onShoot({ x: muzzleX, y: muzzleY, dir: this.facingX });
      }
    }
  }

  preUpdate(t: number, dt: number) {
    super.preUpdate(t, dt);
    // Keep the shield ring stuck to the dino while it's active.
    if (this.shieldRing) {
      this.shieldRing.setPosition(this.x, this.y - 18);
    }
  }
}
